import { motion } from 'framer-motion';
import { Check, Clock, Truck, Package, CreditCard } from 'lucide-react';
import type { Order, OrderStatus } from '../../types/order';
import { formatDate } from '../../utils/formatting';

// --- Status progression order ---

const STATUS_ORDER: OrderStatus[] = ['pending', 'paid', 'processing', 'shipped', 'delivered'];

function statusIndex(status: OrderStatus): number {
  const idx = STATUS_ORDER.indexOf(status);
  return idx === -1 ? 0 : idx;
}

// --- Timeline step definition ---

interface TimelineStep {
  label: string;
  icon: typeof Check;
  statusKey: OrderStatus;
  getTimestamp: (order: Order) => string | undefined;
}

const STEPS: TimelineStep[] = [
  {
    label: 'Order Placed',
    icon: Package,
    statusKey: 'pending',
    getTimestamp: (order) => order.createdAt,
  },
  {
    label: 'Payment Confirmed',
    icon: CreditCard,
    statusKey: 'paid',
    getTimestamp: (order) => order.paidAt,
  },
  {
    label: 'Processing',
    icon: Clock,
    statusKey: 'processing',
    getTimestamp: () => undefined,
  },
  {
    label: 'Shipped',
    icon: Truck,
    statusKey: 'shipped',
    getTimestamp: (order) => order.shippedAt,
  },
  {
    label: 'Delivered',
    icon: Check,
    statusKey: 'delivered',
    getTimestamp: (order) => order.deliveredAt,
  },
];

// --- Tracking link builder ---

function getTrackingUrl(carrier?: string, trackingNumber?: string): string | null {
  if (!trackingNumber) return null;
  const c = (carrier || '').toLowerCase();
  if (c.includes('ups')) return `https://www.ups.com/track?tracknum=${trackingNumber}`;
  if (c.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
  if (c.includes('usps')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
  if (c.includes('dhl')) return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackingNumber}`;
  // Fallback: Google search
  return `https://www.google.com/search?q=${encodeURIComponent(trackingNumber)}+tracking`;
}

// --- Component ---

export function OrderTimeline({ order }: { order: Order }) {
  const currentIdx = statusIndex(order.status);

  return (
    <div className="relative pl-2">
      {STEPS.map((step, i) => {
        const stepIdx = statusIndex(step.statusKey);
        const isComplete = currentIdx >= stepIdx;
        const isCurrent = currentIdx === stepIdx;
        const isLast = i === STEPS.length - 1;
        const timestamp = step.getTimestamp(order);
        const Icon = step.icon;

        return (
          <motion.div
            key={step.statusKey}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1, duration: 0.35 }}
            className="relative flex items-start gap-4 pb-8 last:pb-0"
          >
            {/* Vertical connecting line */}
            {!isLast && (
              <div
                className={`absolute left-[15px] top-[32px] w-0.5 h-[calc(100%-24px)] ${
                  isComplete && !isCurrent ? 'bg-optimal' : 'bg-bg-tertiary'
                }`}
              />
            )}

            {/* Circle indicator */}
            <div className="relative z-10 flex-shrink-0">
              <div
                className={`w-[30px] h-[30px] rounded-full flex items-center justify-center border-2 transition-colors ${
                  isComplete
                    ? 'bg-optimal border-optimal'
                    : 'bg-bg-deep border-bg-tertiary'
                } ${isCurrent ? 'ring-2 ring-optimal/40 animate-pulse' : ''}`}
              >
                {isComplete ? (
                  <Check size={14} className="text-bg-deep" strokeWidth={3} />
                ) : (
                  <Icon size={14} className="text-text-muted" />
                )}
              </div>
            </div>

            {/* Step content */}
            <div className="pt-1 min-w-0">
              <p
                className={`text-base font-medium leading-tight ${
                  isComplete ? 'text-text-primary' : 'text-text-muted'
                } ${isCurrent ? 'text-optimal' : ''}`}
              >
                {step.label}
              </p>

              {/* Timestamp */}
              {timestamp && (
                <p className="text-sm text-text-muted mt-0.5">
                  {formatDate(timestamp)}
                </p>
              )}

              {/* Tracking info for shipped step */}
              {step.statusKey === 'shipped' && isComplete && order.trackingNumber && (
                <div className="mt-2">
                  {(() => {
                    const url = getTrackingUrl(order.trackingCarrier, order.trackingNumber);
                    return (
                      <a
                        href={url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-accent-light hover:underline"
                      >
                        <Truck size={14} />
                        <span>
                          {order.trackingCarrier && (
                            <span className="font-medium">{order.trackingCarrier}: </span>
                          )}
                          <span className="font-mono">{order.trackingNumber}</span>
                        </span>
                      </a>
                    );
                  })()}
                </div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
